import { toSignal } from "@angular/core/rxjs-interop";
import { catchError, fromEvent, map, of, tap } from "rxjs";
import { computed, effect, inject, Injectable, signal, untracked } from "@angular/core";

import { isSidebarInlineViewport } from "@azm/core/constants/sidebar-overlay";
import { isInternalAppType } from "@azm/core/enums/app-types";
import { AuthService } from "@azm/core/services/auth";
import { HttpService, loaderContext } from "@azm/core/services/http";
import { ISidebarItem } from "@azm/core/interfaces/sidebar-item";
import { mergeInternalSidebarItems } from "@azm/core/utilities/merge-internal-sidebar-items";

import { environment } from "@azm/environments/environment";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "azm-sidebar-collapsed";

export interface ISidebarResponse {
	hasPrivacyPolicy: boolean;
	hasTermsAndConditions: boolean;
	menuItems: ISidebarItem[];
}

@Injectable({
	providedIn: "root",
})
export class SidebarService extends HttpService {
	readonly #authService = inject(AuthService);

	protected override get controllerName(): string {
		return "sidebar";
	}

	readonly #isInlineViewport = toSignal(
		fromEvent(window, "resize").pipe(map((event) => isSidebarInlineViewport((event.target as Window).innerWidth))),
		{ initialValue: isSidebarInlineViewport(window.innerWidth) },
	);
	readonly #sidebarOpened = signal(isSidebarInlineViewport(window.innerWidth));
	readonly #desktopCollapsed = signal(readStoredDesktopCollapsed());
	sidebarOpened = this.#sidebarOpened.asReadonly();
	readonly isInlineViewport = this.#isInlineViewport;
	readonly isDesktopCollapsed = computed(() => this.#isInlineViewport() && this.#desktopCollapsed());
	readonly isExpanded = computed(() => (this.#isInlineViewport() ? !this.#desktopCollapsed() : this.#sidebarOpened()));

	#menuItems = signal<ISidebarResponse | null>(null);

	constructor() {
		super();
		effect(() => {
			const isInline = this.#isInlineViewport();
			untracked(() => this.#applyViewport(isInline));
		});
		effect(() => persistDesktopCollapsed(this.#desktopCollapsed()));
	}

	open() {
		if (this.#isInlineViewport()) {
			this.#desktopCollapsed.set(false);
			this.#sidebarOpened.set(true);
			return;
		}

		this.#sidebarOpened.set(true);
		this.removeScrollWhenOpen();
	}

	close() {
		this.#sidebarOpened.set(false);
		this.removeScrollWhenOpen();
	}

	toggle() {
		if (this.#isInlineViewport()) {
			this.#desktopCollapsed.update((collapsed) => !collapsed);
			this.#sidebarOpened.set(true);
			return;
		}

		this.#sidebarOpened.update((isSidebarOpened) => !isSidebarOpened);
		this.removeScrollWhenOpen();
	}

	#applyViewport(isInline: boolean) {
		if (!isInline) {
			this.#sidebarOpened.set(false);
			this.removeScrollWhenOpen();
			return;
		}

		this.#sidebarOpened.set(true);
		this.removeScrollWhenOpen();
	}

	removeScrollWhenOpen() {
		if (isSidebarInlineViewport(window.innerWidth)) return;

		if (this.#sidebarOpened()) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "auto";
		}
	}

	reset() {
		this.#menuItems.set(null);
	}

	sidebarItems(options?: { clearOnError?: boolean }) {
		const cached = this.#menuItems();
		if (cached) return of(cached);

		return this.get<ISidebarResponse>({
			APIName: isInternalAppType(environment.defaultAppType) ? "" : `/${this.#authService.userRole}`,
			context: loaderContext("sidebar"),
		}).pipe(
			tap((menuItems) => this.#menuItems.set(this.#withFrontendMenuItems(menuItems ?? null))),
			catchError(() => {
				if (options?.clearOnError !== false) {
					this.#authService.clear();
				}
				return of(null);
			}),
		);
	}

	#withFrontendMenuItems(response: ISidebarResponse | null): ISidebarResponse | null {
		if (!response) return response;

		return {
			...response,
			menuItems: mergeInternalSidebarItems(
				response.menuItems ?? [],
				this.#authService.isInternalUser,
				this.translocoService.translate("notices.title"),
				this.translocoService.translate("professional_monitoring.title"),
				{
					title: this.translocoService.translate("auditLog.sidebar.title"),
					transactions: this.translocoService.translate("auditLog.transactions.title"),
					general: this.translocoService.translate("auditLog.general.title"),
				},
				this.translocoService.translate("conversationHistory.title"),
			),
		};
	}
}

function readStoredDesktopCollapsed(): boolean {
	if (typeof localStorage === "undefined") return false;
	return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
}

function persistDesktopCollapsed(collapsed: boolean): void {
	if (typeof localStorage === "undefined") return;
	localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
}
